<template>
    <div class="forgot-password-container">
        <div class="forgot-password-form">
            <div class="logo-container">
                <img src="@/assets/images/sellhub-logo.png" alt="SellHub Logo" class="logo" />
            </div>

            <h1>Récupération de mot de passe</h1>

            <div v-if="success" class="success-message">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                <p>Un lien de réinitialisation a été envoyé à votre adresse email. Veuillez vérifier votre boîte de
                    réception.</p>
            </div>

            <div v-if="error && !success" class="error-message">
                {{ error }}
            </div>

            <form v-if="!success" @submit.prevent="handleSubmit">
                <div class="form-group">
                    <label for="email">Email</label>
                    <div class="input-wrapper">
                        <span class="input-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z">
                                </path>
                                <polyline points="22,6 12,13 2,6"></polyline>
                            </svg>
                        </span>
                        <input id="email" v-model="email" type="email" placeholder="Votre adresse email"
                            :class="{ 'input-error': validationErrors.email }" required />
                    </div>
                    <span v-if="validationErrors.email" class="field-error">{{ validationErrors.email[0] }}</span>
                </div>

                <button type="submit" class="submit-button" :disabled="isLoading">
                    <span v-if="isLoading" class="loader"></span>
                    <span>{{ isLoading ? 'Envoi en cours...' : 'Envoyer le lien de réinitialisation' }}</span>
                </button>
            </form>

            <div class="login-link">
                <p>Vous vous souvenez de votre mot de passe ? <router-link :to="{ name: 'login' }">Se
                        connecter</router-link></p>
            </div>
        </div>

        <footer class="footer">
            <p>© {{ new Date().getFullYear() }} SellHub. Tous droits réservés.</p>
            <p>Powered by <span class="powered-by">SellHub</span></p>
        </footer>
    </div>
</template>

<script>
import { authService } from '@/services'
import { defineComponent, ref } from 'vue'

export default defineComponent({
    name: 'ForgotPasswordView',

    setup() {
        const email = ref('')
        const isLoading = ref(false)
        const error = ref(null)
        const success = ref(false)
        const validationErrors = ref({})

        const handleSubmit = async () => {
            isLoading.value = true
            error.value = null
            validationErrors.value = {}
            success.value = false

            try {
                // Utiliser l'action du store pour envoyer la demande de réinitialisation
                await authService.requestResetPasswordLink(email.value)

                // Marquer comme succès
                success.value = true
            } catch (err) {
                console.error('Erreur lors de la demande de réinitialisation :', err)

                if (err.response && err.response.status === 422) {
                    validationErrors.value = err.response.data.errors || {}
                    error.value = err.response.data.message || 'Erreur de validation'
                } else {
                    error.value = err.response?.data?.message || 'Une erreur est survenue lors de la demande de réinitialisation'
                }
            } finally {
                isLoading.value = false
            }
        }

        return {
            email,
            isLoading,
            error,
            success,
            validationErrors,
            handleSubmit
        }
    }
})
</script>

<style scoped>
.forgot-password-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background-color: #f8f9fa;
    font-family: 'Poppins', 'sans-serif';
}

.forgot-password-form {
    width: 100%;
    max-width: 500px;
    background-color: white;
    border-radius: 10px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
    padding: 30px;
    margin: 20px;
}

.logo-container {
    text-align: center;
    margin-bottom: 30px;
}

.logo {
    height: 50px;
    width: auto;
}

h1 {
    color: #1e3a8a;
    text-align: center;
    font-size: 24px;
    font-weight: 600;
    margin-bottom: 30px;
}

.form-group {
    margin-bottom: 20px;
}

label {
    display: block;
    font-size: 16px;
    color: #1e3a8a;
    margin-bottom: 8px;
    font-weight: 500;
}

.input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.input-icon {
    position: absolute;
    left: 14px;
    color: #0A5C6B;
}

input {
    width: 100%;
    padding: 16px 45px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 16px;
    background-color: #f8fafc;
    color: #1e293b;
    transition: border-color 0.3s, box-shadow 0.3s;
}

input:focus {
    outline: none;
    border-color: #3FDA4D;
    box-shadow: 0 0 0 3px rgba(63, 218, 77, 0.2);
}

input::placeholder {
    color: #a0aec0;
}

.input-error {
    border-color: #ef4444 !important;
}

.field-error {
    display: block;
    color: #ef4444;
    font-size: 14px;
    margin-top: 5px;
}

.submit-button {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 16px;
    background-color: #0A5C6B;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.3s;
    margin-bottom: 20px;
}

.submit-button:hover:not(:disabled) {
    background-color: #3FDA4D;
}

.submit-button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.error-message {
    background-color: #fee2e2;
    color: #b91c1c;
    padding: 12px;
    border-radius: 8px;
    margin-bottom: 20px;
    font-size: 14px;
}

.success-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: #d1fae5;
    color: #065f46;
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 20px;
    font-size: 14px;
    text-align: center;
}

.success-message svg {
    color: #059669;
    margin-bottom: 10px;
}

.login-link {
    text-align: center;
    color: #475569;
    font-size: 14px;
}

.login-link a {
    color: #0A5C6B;
    text-decoration: none;
    font-weight: 600;
    transition: color 0.3s;
}

.login-link a:hover {
    color: #3FDA4D;
    text-decoration: underline;
}

.footer {
    text-align: center;
    color: #64748b;
    font-size: 12px;
    margin-top: 20px;
    padding: 20px;
}

.powered-by {
    color: #3FDA4D;
    font-weight: 600;
}

.loader {
    width: 20px;
    height: 20px;
    border: 2px solid #ffffff;
    border-bottom-color: transparent;
    border-radius: 50%;
    display: inline-block;
    margin-right: 10px;
    animation: rotation 1s linear infinite;
}

@keyframes rotation {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

@media (max-width: 480px) {
    .forgot-password-form {
        padding: 20px;
        margin: 10px;
    }

    h1 {
        font-size: 22px;
    }

    input {
        padding: 14px 40px;
    }

    .submit-button {
        padding: 14px;
    }
}
</style>